import React, {
	createContext,
	useReducer,
	useContext,
	ReactNode,
	useEffect,
} from "react"
import { uploadService } from "@/services/uploadService"
import { useAuth } from "@/hooks/useAuth"

/**
 * Represents an individual file upload task.
 */
export interface UploadTask {
	/** Unique identifier for the task. */
	id: string
	/** Name of the file being uploaded. */
	fileName: string
	/** Size of the file in bytes. */
	fileSize: number
	/** MIME type of the file. */
	fileType: string
	/** Last modified timestamp of the file. */
	fileLastModified: number
	/** Identifier for the associated case. */
	caseId: string
	/** The GCS resumable session URI. Optional, as it's set after initiation. */
	sessionUri?: string
	/** Number of bytes successfully uploaded. */
	uploadedBytes: number
	/** Current status of the upload. */
	status:
		| "pending" // Waiting to start
		| "uploading" // Actively uploading
		| "paused" // Paused by user or system
		| "error" // An error occurred
		| "completed" // Successfully uploaded
		| "pending_resume" // Waiting for user to re-select file to resume
		| "aborted" // User cancelled the upload
	/** Error message if the status is 'error'. */
	error?: string
	progress: number // Percentage 0-100
	file?: File // The actual file, not stored in localStorage
	/** Relative path for files uploaded as part of a folder (e.g., "folder/subfolder/file.pdf") */
	relativePath?: string
}

/**
 * Defines the shape of the global upload state, containing all tasks.
 */
interface UploadState {
	/** Array of current upload tasks. */
	tasks: UploadTask[]
}

/**
 * Defines the actions that can be dispatched to modify the upload state.
 */
export type UploadAction =
	/** Adds a new file to the upload queue. */
	| {
			type: "ADD_TASK"
			payload: {
				task: Omit<UploadTask, "status" | "progress" | "uploadedBytes" | "id">
				file: File
			}
	  }
	/** Updates the status (and optionally error message) of an existing task. */
	| {
			type: "UPDATE_TASK_STATUS"
			payload: { id: string; status: UploadTask["status"]; error?: string }
	  }
	/** Updates the progress and uploaded bytes of an existing task. */
	| {
			type: "UPDATE_TASK_PROGRESS"
			payload: { id: string; progress: number; uploadedBytes: number }
	  }
	/** Sets the GCS session URI for a task after successful initiation. */
	| { type: "SET_SESSION_URI"; payload: { id: string; sessionUri: string } }
	/** Removes a task from the queue (e.g., user cancellation). */
	| { type: "REMOVE_TASK"; payload: { id: string } }
	/** Rehydrates tasks from localStorage on application load. */
	| { type: "REHYDRATE_TASKS"; payload: { tasks: UploadTask[] } }
	/** Associates a re-selected File object with a task that is pending resume. */
	| { type: "SET_FILE_FOR_RESUME"; payload: { id: string; file: File } }

const emptyState: UploadState = {
	tasks: [],
}

/**
 * Reducer function to manage state changes for uploads.
 * @param state - The current upload state.
 * @param action - The action to perform.
 * @returns The new upload state.
 */
export const uploadReducer = (
	state: UploadState,
	action: UploadAction
): UploadState => {
	switch (action.type) {
		case "ADD_TASK": {
			const newTask: UploadTask = {
				...action.payload.task,
				id: `${action.payload.task.fileName}-${
					action.payload.task.fileLastModified
				}-${Date.now()}`,
				status: "pending",
				progress: 0,
				uploadedBytes: 0,
				file: action.payload.file,
			}
			return { ...state, tasks: [...state.tasks, newTask] }
		}
		case "UPDATE_TASK_STATUS":
			return {
				...state,
				tasks: state.tasks.map((task) =>
					task.id === action.payload.id
						? {
								...task,
								status: action.payload.status,
								error: action.payload.error,
						  }
						: task
				),
			}
		case "UPDATE_TASK_PROGRESS":
			return {
				...state,
				tasks: state.tasks.map((task) =>
					task.id === action.payload.id
						? {
								...task,
								progress: action.payload.progress,
								uploadedBytes: action.payload.uploadedBytes,
						  }
						: task
				),
			}
		case "SET_SESSION_URI":
			return {
				...state,
				tasks: state.tasks.map((task) =>
					task.id === action.payload.id
						? { ...task, sessionUri: action.payload.sessionUri }
						: task
				),
			}
		case "REMOVE_TASK":
			return {
				...state,
				tasks: state.tasks.filter((task) => task.id !== action.payload.id),
			}
		case "REHYDRATE_TASKS":
			return { ...state, tasks: action.payload.tasks }
		case "SET_FILE_FOR_RESUME":
			return {
				...state,
				tasks: state.tasks.map((task) =>
					task.id === action.payload.id
						? {
								...task,
								file: action.payload.file,
								status: "pending_resume",
						  }
						: task
				),
			}
		default:
			return state
	}
}

export const getInitialStateFromLocalStorage = (
	initial: UploadState
): UploadState => {
	try {
		const storedTasks = localStorage.getItem(LOCAL_STORAGE_KEY)
		if (storedTasks) {
			const parsedTasks: UploadTask[] = JSON.parse(storedTasks)
			// Mark tasks that were in progress as pending_resume
			// Clear File objects as they are not serializable
			const rehydratedTasks = parsedTasks.map((task) => ({
				...task,
				file: undefined, // File object cannot be stored
				status:
					(task.status === "uploading" ||
						task.status === "paused" ||
						task.status === "error") &&
					task.sessionUri
						? "pending_resume"
						: task.status,
				progress:
					(task.status === "uploading" ||
						task.status === "paused" ||
						task.status === "pending_resume") &&
					task.sessionUri
						? task.progress
						: task.status === "completed"
						? 100
						: 0,
			}))
			return { ...initial, tasks: rehydratedTasks }
		}
	} catch (error) {
		console.error("Failed to load tasks from localStorage:", error)
	}
	return emptyState
}

/**
 * Context for managing global file upload state.
 */
interface UploadContextType {
	/** The current state of all upload tasks. */
	uploadState: UploadState
	/** Dispatch function to update the upload state. */
	dispatchUploadAction: React.Dispatch<UploadAction>
}

export const UploadContext = createContext<UploadContextType | undefined>(
	undefined
)

export const LOCAL_STORAGE_KEY = "uploadTasks"

/**
 * Custom hook to access the UploadContext.
 * Throws an error if used outside of an UploadProvider.
 * @returns The upload context, including state and dispatch function.
 */
export const useUpload = (): UploadContextType => {
	const context = useContext(UploadContext)
	if (context === undefined) {
		throw new Error("useUpload must be used within an UploadProvider")
	}
	return context
}

export { emptyState }
